import React, { FC, useMemo, useRef } from "react";
import { Formik, FormikProps } from "formik";
import { Col, Grid, Row } from "rsuite";
import { PhoneInput } from "@shared/components/Common/PhoneInput";
import {
  AccessControl,
  AsyncCheckPickerInput,
  Button,
  DatePickerInput,
  DateRangePickerInput,
  ErrorMessageField,
  FieldPreview,
  HorizontalDivider,
  Icon,
  Input,
  RadioGroup,
  SelectPickerInput,
} from "@shared/components";
import { Hospital, Patient, Station, User } from "@shared/models";
import { GENDER_OPTIONS, PERMISSION, TIMEZONE_OPTIONS, TRACKING_PARAMETER_SETTINGS } from "@shared/constants";
import { CreatePatientDto, PatientFormShape } from "@containers/Patient/interfaces";
import { useCheckPermission } from "@shared/hooks";
import { StationConnectionResult } from "@containers/Station/interfaces";
import { TrackingTimeRangeWarning } from "@containers/Station/components";

import { getHandlers, getInitValues, prepareSubmitValue, validationSchema } from "./formHelpers";
import { SensorsPreview, StationsPreview } from "../../PatientDetailsContainer/PatientDetailsSidebar/components";

import "./index.scss";

interface PatientFormProps {
  loading: boolean;
  isConnectionsLoading: boolean;
  authorizedUser: User;
  patient: Patient | null;
  hospital: Hospital | null;
  stationsConnections: Record<string, StationConnectionResult> | null;
  onChangeStatus: (patientId: number) => void;
  onChangeForm: () => void;
  onCloseForm: () => void;
  onSubmitForm: (payload: CreatePatientDto) => void;
}

const PatientForm: FC<PatientFormProps> = (props) => {
  const {
    patient,
    hospital,
    onChangeForm,
    onCloseForm,
    onSubmitForm,
    onChangeStatus,
    authorizedUser,
    stationsConnections,
    loading,
    isConnectionsLoading,
  } = props;

  const formikRef = useRef<FormikProps<PatientFormShape>>(null);

  const canViewUsersList = useCheckPermission(PERMISSION.VIEW_USERS, { hospital });
  const canCreateEditPatient = useCheckPermission(patient ? PERMISSION.EDIT_PATIENT : PERMISSION.CREATE_PATIENT, {
    hospital,
  });
  const canSetUpStablePeriod = useCheckPermission(PERMISSION.SET_UP_STABLE_PERIOD, { hospital });

  const stations = patient?.stations || [];
  const station: Station | null = stations.find(Boolean) || null;
  const stationConnection = stationsConnections?.[station?.station_code || ""] || null;

  const formValues = useMemo(() => {
    return getInitValues(patient, authorizedUser, canViewUsersList);
  }, [patient, authorizedUser, canViewUsersList]);

  const handlers = useMemo(() => {
    return hospital ? getHandlers(hospital) : null;
  }, [hospital]);

  const initAssignedUsersOptions = useMemo(() => formValues.assigned_users, [formValues]);

  return (
    <Formik
      innerRef={formikRef}
      validationSchema={validationSchema}
      enableReinitialize={true}
      validateOnBlur={false}
      onSubmit={(values, { setSubmitting }) => {
        onSubmitForm(prepareSubmitValue(values, !patient ? hospital : null));
        setSubmitting(false);
      }}
      initialValues={formValues}
      validateOnChange={true}
      validate={() => {
        onChangeForm();
      }}
    >
      {({ values, handleChange, handleBlur, handleSubmit, setFieldValue, errors }) => (
        <div className="patient-form-wrapper">
          <form className={!patient ? "patient-form" : undefined} onSubmit={handleSubmit}>
            <div className="patient-form-header">
              <div className="patient-form-header-left">{patient ? "Edit Patient" : "New Patient"}</div>
              <div className="patient-form-header-right">
                <AccessControl permission={patient ? PERMISSION.EDIT_PATIENT : PERMISSION.CREATE_PATIENT}>
                  <Button type="submit" className="action-btn" loading={loading} disabled={loading}>
                    {patient ? "Save" : "Create"}
                  </Button>
                </AccessControl>
                {!loading ? (
                  <Button className="close-btn" onClick={() => onCloseForm()}>
                    <Icon type="clear" />
                  </Button>
                ) : null}
              </div>
            </div>
            <Grid fluid className="patient-form-content">
              <Row>
                <Col xs={12}>
                  <Input
                    disabled={!canCreateEditPatient || loading}
                    type="text"
                    labelTitle="First Name*"
                    name="first_name"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.first_name}
                    placeholder="First Name"
                  />
                  <ErrorMessageField name="first_name" />
                </Col>
                <Col xs={12}>
                  <Input
                    disabled={!canCreateEditPatient || loading}
                    type="text"
                    labelTitle="Last Name*"
                    name="last_name"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.last_name}
                    placeholder="Last Name"
                  />
                  <ErrorMessageField name="last_name" />
                </Col>
              </Row>
              <Row>
                <Col xs={24}>
                  <Input
                    disabled={!canCreateEditPatient || loading}
                    type="text"
                    labelTitle="Patient ID"
                    name="patient_code"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.patient_code || ""}
                    placeholder="Patient ID"
                  />
                  <ErrorMessageField name="patient_code" />
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <SelectPickerInput
                    disabled={!canCreateEditPatient || loading}
                    searchable={false}
                    name="gender"
                    data={GENDER_OPTIONS}
                    onChange={(value) => setFieldValue("gender", value)}
                    onClean={handleChange}
                    labelTitle="Gender*"
                    value={values.gender}
                    placeholder="Gender"
                  />
                  <ErrorMessageField name="gender" />
                </Col>
                <Col xs={12}>
                  <Input
                    disabled={!canCreateEditPatient || loading}
                    type="text"
                    labelTitle="Age*"
                    name="age"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.age}
                    placeholder="Age"
                  />
                  <ErrorMessageField name="age" />
                </Col>
              </Row>
              <Row>
                <Col xs={24}>
                  <PhoneInput
                    labelTitle="Phone Number"
                    disabled={!canCreateEditPatient || loading}
                    onChange={(v: string) => setFieldValue("phone", v)}
                    onBlur={handleBlur}
                    value={values.phone || ""}
                    placeholder="15055555555"
                  />
                  <ErrorMessageField name="phone" />
                </Col>
              </Row>
              <Row>
                <Col xs={24}>
                  <Input
                    disabled={!canCreateEditPatient || loading}
                    type="text"
                    labelTitle="Email"
                    name="email"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email || ""}
                    placeholder="Email"
                  />
                  <ErrorMessageField name="email" />
                </Col>
              </Row>
              <Row>
                <Col xs={24}>
                  <SelectPickerInput
                    disabled={!canCreateEditPatient || loading}
                    searchable
                    name="timezone"
                    data={TIMEZONE_OPTIONS}
                    onChange={(value) => setFieldValue("timezone", value)}
                    onClean={handleChange}
                    labelTitle="Time Zone"
                    value={values.timezone}
                    placeholder="Time Zone"
                    placement="auto"
                  />
                  <ErrorMessageField name="timezone" />
                </Col>
              </Row>

              {handlers ? (
                <Row>
                  <Col xs={24}>
                    <AsyncCheckPickerInput
                      disabled={!canCreateEditPatient || loading || !canViewUsersList}
                      labelTitle="Assigned Staff*"
                      name="assigned_users"
                      className="select-picker"
                      placeholder="Select Staff"
                      values={values.assigned_users}
                      getData={handlers.assigned_users.getData}
                      selectData={handlers.assigned_users.selectData}
                      onChange={(v) => setFieldValue("assigned_users", v)}
                      prepareOptionFunction={handlers.assigned_users.prepareOptionFunction}
                      initOptions={initAssignedUsersOptions}
                      placement="auto"
                    />
                    <ErrorMessageField name="assigned_users" />
                  </Col>
                </Row>
              ) : null}
              <HorizontalDivider />
              <Row>
                <TrackingTimeRangeWarning stationConnection={stationConnection} isLoading={isConnectionsLoading} />
                <div className="group-label tracking-time-range">Tracking time Range</div>
                <div className="group-label">Please set up the time according to the patient&apos;s timezone.</div>
                <Col xs={8}>
                  <DatePickerInput
                    disabled={true}
                    value={values.station_start_time}
                    onChange={(v) => setFieldValue("station_start_time", v)}
                    labelTitle="Start Time"
                    labelClassName="patient-details-dp-label"
                    format="hh:mm aa"
                    showMeridian
                    placement="auto"
                  />
                </Col>
                <Col xs={8}>
                  <DatePickerInput
                    disabled={true}
                    value={values.station_end_time}
                    onChange={(v) => setFieldValue("station_end_time", v)}
                    labelTitle="End Time"
                    labelClassName="patient-details-dp-label"
                    format="hh:mm aa"
                    showMeridian
                    placement="auto"
                  />
                </Col>
                <Col xs={8}>
                  <SelectPickerInput
                    disabled={true}
                    searchable
                    name="timezone"
                    data={TIMEZONE_OPTIONS}
                    onChange={(value) => setFieldValue("station_timezone", value)}
                    onClean={handleChange}
                    labelTitle="Time Zone"
                    value={values.station_timezone}
                    placeholder="Time Zone"
                    placement="auto"
                  />
                </Col>
              </Row>
              <HorizontalDivider />
              <Row>
                <div className="group-label">HR and RR for Stable Period</div>
                <Col xs={24}>
                  <RadioGroup
                    disabled={!canCreateEditPatient || !canSetUpStablePeriod || loading}
                    options={TRACKING_PARAMETER_SETTINGS}
                    value={values.tracking_parameter_settings}
                    onChange={(v) => setFieldValue("tracking_parameter_settings", v)}
                  />
                  <ErrorMessageField name="tracking_parameter_settings" />
                </Col>
              </Row>
              <Row>
                {values.tracking_parameter_settings.value === TRACKING_PARAMETER_SETTINGS[0].value ? (
                  <Col xs={24}>
                    <DateRangePickerInput
                      disabled={!canCreateEditPatient || !canSetUpStablePeriod || loading}
                      labelTitle="Select date range"
                      placement="auto"
                      value={values.start_end_date}
                      onChange={(v) => setFieldValue("start_end_date", v)}
                    />
                    {errors.start_end_date && <ErrorMessageField name="start_end_date" />}
                  </Col>
                ) : (
                  <>
                    <Col xs={12}>
                      <Input
                        disabled={!canCreateEditPatient || !canSetUpStablePeriod || loading}
                        type="text"
                        labelTitle="HR for Stable Period*"
                        name="hr_stable"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.hr_stable}
                        placeholder="HR for Stable Period"
                      />
                      <ErrorMessageField name="hr_stable" />
                    </Col>
                    <Col xs={12}>
                      <Input
                        disabled={!canCreateEditPatient || !canSetUpStablePeriod || loading}
                        type="text"
                        labelTitle="RR for Stable Period*"
                        name="rr_stable"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.rr_stable}
                        placeholder="RR for Stable Period"
                      />
                      <ErrorMessageField name="rr_stable" />
                    </Col>
                  </>
                )}
              </Row>
              {patient ? (
                <Row>
                  <HorizontalDivider />
                  <FieldPreview
                    className="stations-row"
                    label="SDH"
                    value={<StationsPreview stations={stations || []} />}
                  />
                  <FieldPreview
                    className="sensors-row"
                    label="Vital Signs"
                    value={<SensorsPreview stations={stations} />}
                  />
                  <HorizontalDivider />
                </Row>
              ) : null}
            </Grid>
          </form>
          <AccessControl permission={PERMISSION.DELETE_PATIENT}>
            {patient ? (
              <div className="patient-form-footer">
                <Button
                  disabled={loading}
                  className={patient.is_active ? "deactivate" : "activate"}
                  onClick={() => onChangeStatus(Number(patient.id))}
                >
                  {patient.is_active ? "Deactivate" : "Activate"}
                </Button>
              </div>
            ) : null}
          </AccessControl>
        </div>
      )}
    </Formik>
  );
};

export default PatientForm;
